/**
 * Enums del dominio. El orden de los valores importa donde son escalas
 * acumulativas (roles), y no importa en el resto.
 *
 * Los que tienen valores "en standby" los declaran completos: el CHECK de la
 * base es el que restringe qué se acepta hoy, para que habilitarlos después
 * sea borrar una constraint y no migrar datos.
 */

export const USUARIO_ROL = ['admin', 'organizador', 'user'] as const
export type UsuarioRol = (typeof USUARIO_ROL)[number]

/** Roles habilitados en esta etapa. `organizador` queda en standby. */
export const USUARIO_ROL_ACTIVOS: readonly UsuarioRol[] = ['admin', 'user']

export const USUARIO_ESTADO = ['sin_acceso', 'invitado', 'activo', 'suspendido'] as const
export type UsuarioEstado = (typeof USUARIO_ESTADO)[number]

/** Único estado que puede iniciar sesión: los demás no tienen contraseña o están bloqueados. */
export const USUARIO_ESTADO_PUEDE_INGRESAR: readonly UsuarioEstado[] = ['activo']

/** Estados que ya tienen contraseña definida, y por eso no pueden quedarse sin email. */
export const USUARIO_ESTADO_CON_PASSWORD: readonly UsuarioEstado[] = ['activo', 'suspendido']

export const CREDENCIAL_TIPO = ['definir_password', 'reset_password'] as const
export type CredencialTipo = (typeof CREDENCIAL_TIPO)[number]

/**
 * Género del jugador. Opcional en todos lados: es un dato de la persona, no de
 * su cuenta, y nadie se queda sin inscribir por no tenerlo cargado.
 *
 * Es cerrado —a diferencia del país, que va como texto libre— porque la razón
 * de guardarlo es armar categorías: una rama femenina, un ranking por rama. Con
 * texto libre habría que normalizar a mano lo que escribió cada uno antes de
 * poder agrupar por algo.
 *
 * Son los dos valores con los que el dominó arma sus ramas. Un tercer valor no
 * tendría rama a la que ir, así que dejaría al jugador fuera de todo lo que
 * este campo existe para calcular.
 */
export const GENERO = ['femenino', 'masculino'] as const
export type Genero = (typeof GENERO)[number]

export const TORNEO_TIPO = ['suizo', 'muerte_subita', 'rotados'] as const
export type TorneoTipo = (typeof TORNEO_TIPO)[number]

/** Tipos con tablas de desarrollo implementadas. Los otros dos van a tablas propias. */
export const TORNEO_TIPO_ACTIVOS: readonly TorneoTipo[] = ['suizo']

export const TORNEO_MODALIDAD = ['individual', 'parejas', 'equipos'] as const
export type TorneoModalidad = (typeof TORNEO_MODALIDAD)[number]

export const TORNEO_ESTADO = [
  'borrador',
  'inscripcion',
  'en_curso',
  'finalizado',
  'cancelado',
] as const
export type TorneoEstado = (typeof TORNEO_ESTADO)[number]

/** Estados en los que todavía se puede modificar la nómina y la configuración. */
export const TORNEO_ESTADO_ABIERTO: readonly TorneoEstado[] = ['borrador', 'inscripcion']

/** Escalera acumulativa: director ⊃ arbitro ⊃ anotador ⊃ veedor. */
export const STAFF_ROL = ['director', 'arbitro', 'anotador', 'veedor'] as const
export type StaffRol = (typeof STAFF_ROL)[number]

/** Rol habilitado en esta etapa. Los otros tres quedan en standby. */
export const STAFF_ROL_ACTIVOS: readonly StaffRol[] = ['director']

/**
 * Situación de una persona en UN torneo.
 *
 * No tiene nada que ver con `USUARIO_ESTADO`, que responde otra pregunta —
 * "¿puede entrar al sistema?". Son dos ejes independientes: alguien puede
 * estar `activo` en la app y `suspendido` en un torneo, o `sin_acceso` a la
 * app y `confirmado` en el salón. Que los dos tengan un valor llamado
 * `suspendido` es coincidencia de nombre, no de significado.
 *
 * El organizador maneja las dos puntas: anota (`pendiente`) y el día del
 * evento confirma (`confirmado`). El jugador nunca pide lugar.
 */
export const JUGADOR_ESTADO = [
  'pendiente',
  'confirmado',
  'ausente',
  'retirado',
  'suspendido',
] as const
export type JugadorEstado = (typeof JUGADOR_ESTADO)[number]

/** Estados que exigen fecha de confirmación, y los que exigen no tenerla. */
export const JUGADOR_ESTADO_CON_CHECKIN: readonly JugadorEstado[] = ['confirmado', 'retirado']
export const JUGADOR_ESTADO_SIN_CHECKIN: readonly JugadorEstado[] = ['pendiente', 'ausente']

/** Estados desde los que el jugador todavía puede llegar a competir. */
export const JUGADOR_ESTADO_EN_JUEGO: readonly JugadorEstado[] = ['pendiente', 'confirmado']

/** Estados de los que no se vuelve en este torneo. */
export const JUGADOR_ESTADO_FUERA: readonly JugadorEstado[] = ['ausente', 'retirado', 'suspendido']

/**
 * Las cuatro tarjetas del árbitro, en orden de gravedad.
 *
 * `verbal` y `amarilla` quedan asentadas y no cambian el estado. `roja` y
 * `negra` son terminales: sacan al jugador del torneo y retiran a su
 * participante. Lo que las separa es el alcance —la roja se agota con el
 * torneo, la negra sigue pesando hasta su fecha de vencimiento— y por eso solo
 * la negra lleva `venceAt`.
 *
 * El orden importa: es una escala, y `SANCION_SIGUIENTE` la recorre.
 */
export const SANCION_TIPO = ['verbal', 'amarilla', 'roja', 'negra'] as const
export type SancionTipo = (typeof SANCION_TIPO)[number]

/** Las que sacan al jugador del torneo en el acto. */
export const SANCION_EXPULSA: readonly SancionTipo[] = ['roja', 'negra']

/** La única que vale más allá del torneo, y por eso la única con vencimiento. */
export const SANCION_CON_VENCIMIENTO: readonly SancionTipo[] = ['negra']

/**
 * Las tarjetas que acompañan una resta de puntos en plena partida. Las que
 * expulsan quedan afuera: con la roja el jugador deja el torneo y su pareja con
 * él, y no hay partida a la que restarle nada.
 */
export const SANCION_CON_RESTA = ['verbal', 'amarilla'] as const satisfies readonly SancionTipo[]

/**
 * Cuántas de una tarjeta equivalen a la de arriba: dos verbales son una
 * amarilla, dos amarillas son una roja.
 */
export const SANCIONES_PARA_SUBIR = 2

/**
 * Qué tarjeta corresponde cuando se juntan `SANCIONES_PARA_SUBIR` de la de
 * abajo. La roja no tiene sucesora acá a propósito: la negra no se acumula, se
 * decide — es la que trasciende al torneo y esa es una resolución aparte, no
 * el resultado de sumar tarjetas.
 */
export const SANCION_SIGUIENTE: Partial<Record<SancionTipo, SancionTipo>> = {
  verbal: 'amarilla',
  amarilla: 'roja',
}

/** El escalón de abajo: de qué tarjeta sale cada una al acumularse. */
export const SANCION_ANTERIOR: Partial<Record<SancionTipo, SancionTipo>> = Object.fromEntries(
  Object.entries(SANCION_SIGUIENTE).map(([desde, hacia]) => [hacia, desde]),
)

export const PARTICIPANTE_ESTADO = ['activo', 'retirado'] as const
export type ParticipanteEstado = (typeof PARTICIPANTE_ESTADO)[number]

export const RONDA_ESTADO = ['pendiente', 'emparejada', 'en_curso', 'cerrada'] as const
export type RondaEstado = (typeof RONDA_ESTADO)[number]

export const MESA_TIPO = ['normal', 'incompleta'] as const
export type MesaTipo = (typeof MESA_TIPO)[number]

export const MESA_ESTADO = ['pendiente', 'cargada'] as const
export type MesaEstado = (typeof MESA_ESTADO)[number]

/**
 * Si los jugadores cargan la partida mano a mano desde la mesa, y qué pasa con
 * eso al terminar. Solo en los torneos que se llevan en la nube: ver la
 * migración 0025 de Postgres.
 *
 * - `desactivada`: nadie carga manos.
 * - `informativa`: se ve cómo va cada mesa; el resultado lo carga el organizador.
 * - `sugerida`: al terminar, el organizador ve el marcador de la mesa y lo aprueba.
 * - `automatica`: al terminar, la mesa queda cargada sola.
 */
export const CARGA_PARCIAL = ['desactivada', 'informativa', 'sugerida', 'automatica'] as const
export type CargaParcial = (typeof CARGA_PARCIAL)[number]

export const RESULTADO_TIPO = ['ganada', 'perdida'] as const
export type ResultadoTipo = (typeof RESULTADO_TIPO)[number]

export const RESULTADO_MOTIVO = [
  'normal',
  'bye',
  'mesa_incompleta',
  'ausencia',
  'abandono',
] as const
export type ResultadoMotivo = (typeof RESULTADO_MOTIVO)[number]

/** Motivos que acreditan puntos sin rival: siempre ganada y con recibidos en 0. */
export const MOTIVO_SIN_RIVAL: readonly ResultadoMotivo[] = ['bye', 'mesa_incompleta']

/** Motivos que siempre implican derrota. */
export const MOTIVO_DERROTA: readonly ResultadoMotivo[] = ['ausencia', 'abandono']

export const ALERTA_SEVERIDAD = ['info', 'warning', 'error'] as const
export type AlertaSeveridad = (typeof ALERTA_SEVERIDAD)[number]

export const ALERTA_TIPO = [
  'limite_companero_excedido',
  'equipo_mismo_mesa',
  'mesa_incompleta',
  'resultado_editado',
  'carga_propia',
  'otro',
] as const
export type AlertaTipo = (typeof ALERTA_TIPO)[number]

export const SYNC_OPERACION = ['upsert', 'delete'] as const
export type SyncOperacion = (typeof SYNC_OPERACION)[number]

/** Cantidad de participantes que se sientan a una mesa, por modalidad. */
export const PARTICIPANTES_POR_MESA: Record<TorneoModalidad, number> = {
  individual: 4,
  parejas: 2,
  equipos: 4,
}

/** Jugadores que hacen falta para que una mesa se juegue. Con menos, es forfeit. */
export const JUGADORES_PARA_JUGAR = 4

/** Mínimo de equipos para que el intercalado nunca repita equipo en una mesa de 4. */
export const MINIMO_EQUIPOS = 4
